import { useState } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { clamp } from "../../../utils/formatUtils";
import type { PdfExportProgress, ShareFileAnalysis, ShareOptimizeOptions, SlideSizeEntry } from "../../../types";
import { formatFileSize, useShareExport, type SharePhase } from "../hooks/useShareExport";
import styles from "./SharePanel.module.css";

const QUALITY_MIN = 10;
const QUALITY_MAX = 100;
const QUALITY_STEP = 5;

const DEFAULT_OPTIONS: ShareOptimizeOptions = {
  compressImages: true,
  imageQuality: 80,
  removeUnusedLayouts: true,
};

interface SharePanelProps {
  readonly onBack?: () => void;
}

export function SharePanel({ onBack }: Readonly<SharePanelProps>) {
  const { state, toast, analyze, optimizeAndDownload, exportPdf } = useShareExport();
  const [options, setOptions] = useState<ShareOptimizeOptions>(DEFAULT_OPTIONS);

  const actionsDisabled = state.phase === "analyzing" || state.busy !== null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader title="Share & Export" subtitle="Deckorate" onBack={onBack} />

        <p className={styles.introBanner}>
          Analyse the deck&rsquo;s file size, then optimise it or export. Optimisation happens
          on a downloaded copy — the open file is untouched.
        </p>

        <AnalyzeButton phase={state.phase} hasAnalysis={state.analysis !== null} onAnalyze={analyze} />

        {state.errorMessage && (
          <div className={`${styles.statusBox} ${styles.statusError}`}>
            <span className={styles.errorLabel}>Error:</span> {state.errorMessage}
          </div>
        )}

        {state.analysis && <FileAnalysisSection analysis={state.analysis} />}

        <OptimisationOptions options={options} onChange={setOptions} disabled={actionsDisabled} />

        <button
          type="button"
          className={styles.btnPrimary}
          disabled={actionsDisabled}
          onClick={() => optimizeAndDownload(options)}
        >
          {state.busy === "optimizing" ? "Optimising…" : "Download Optimised PPTX"}
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          disabled={actionsDisabled}
          onClick={exportPdf}
        >
          {state.busy === "exportingPdf" ? pdfBusyLabel(state.pdfProgress) : "Export as PDF"}
        </button>
      </div>

      {toast && <ShareToast message={toast} />}
    </div>
  );
}

function pdfBusyLabel(progress: PdfExportProgress | null): string {
  if (progress && progress.total > 0) return `Exporting… ${progress.current}/${progress.total}`;
  return "Exporting…";
}

// ─── Analyze File ───────────────────────────────────────────────────────────

interface AnalyzeButtonProps {
  readonly phase: SharePhase;
  readonly hasAnalysis: boolean;
  readonly onAnalyze: () => void;
}

function AnalyzeButton({ phase, hasAnalysis, onAnalyze }: AnalyzeButtonProps) {
  if (phase === "analyzing") {
    return (
      <div className={styles.analyzingRow}>
        <span className={styles.spinnerIcon} aria-hidden="true" />
        <span>Analysing file…</span>
      </div>
    );
  }
  return (
    <button type="button" className={styles.btnSecondary} onClick={onAnalyze}>
      {hasAnalysis ? "Re-analyze File" : "Analyze File"}
    </button>
  );
}

// ─── File analysis ──────────────────────────────────────────────────────────

interface FileAnalysisSectionProps {
  readonly analysis: ShareFileAnalysis;
}

function FileAnalysisSection({ analysis }: FileAnalysisSectionProps) {
  const mediaPercent = clamp(analysis.mediaPercent, 0, 100);
  const largestSlideBytes = analysis.topSlides[0]?.sizeBytes ?? 0;

  return (
    <div className={styles.analysisSection}>
      <div className={styles.sectionLabel}>FILE ANALYSIS</div>

      <div className={styles.analysisHeadline}>
        <span>{formatFileSize(analysis.totalSizeBytes)}</span>
        <span className={styles.analysisSep} aria-hidden="true">|</span>
        <span>{analysis.slideCount} slide{analysis.slideCount === 1 ? "" : "s"}</span>
      </div>

      <div className={styles.mediaRow}>
        <span className={styles.mediaLabel}>
          Media: {formatFileSize(analysis.mediaSizeBytes)} ({mediaPercent}% of total)
        </span>
        <div className={styles.barTrack}>
          <div className={styles.barFillMedia} style={{ width: `${mediaPercent}%` }} />
        </div>
      </div>

      {analysis.topSlides.length > 0 && (
        <div className={styles.breakdown}>
          {analysis.topSlides.map((slide) => (
            <SlideBreakdownRow key={slide.slideIndex} slide={slide} maxBytes={largestSlideBytes} />
          ))}
        </div>
      )}

      {analysis.issues.map((issue) => (
        <div key={issue.kind} className={styles.warningBox}>
          <span aria-hidden="true">⚠</span> {issue.message}
        </div>
      ))}
    </div>
  );
}

interface SlideBreakdownRowProps {
  readonly slide: SlideSizeEntry;
  readonly maxBytes: number;
}

function SlideBreakdownRow({ slide, maxBytes }: SlideBreakdownRowProps) {
  const percent = maxBytes > 0 ? clamp((slide.sizeBytes / maxBytes) * 100, 0, 100) : 0;
  return (
    <div className={styles.breakdownRow}>
      <span className={styles.breakdownLabel}>{slide.slideIndex} {slide.title}</span>
      <div className={styles.barTrack}>
        <div className={styles.barFillSlide} style={{ width: `${percent}%` }} />
      </div>
      <span className={styles.breakdownSize}>{formatFileSize(slide.sizeBytes)}</span>
    </div>
  );
}

// ─── Optimisation options ───────────────────────────────────────────────────

interface OptimisationOptionsProps {
  readonly options: ShareOptimizeOptions;
  readonly onChange: (next: ShareOptimizeOptions) => void;
  readonly disabled: boolean;
}

function OptimisationOptions({ options, onChange, disabled }: OptimisationOptionsProps) {
  return (
    <div className={styles.optionsSection}>
      <div className={styles.sectionLabel}>OPTIMISATION OPTIONS</div>

      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={options.compressImages}
          disabled={disabled}
          onChange={(e) => onChange({ ...options, compressImages: e.target.checked })}
        />
        <span>Compress images</span>
      </label>

      {options.compressImages && (
        <div className={styles.qualityRow}>
          <input
            type="range"
            min={QUALITY_MIN}
            max={QUALITY_MAX}
            step={QUALITY_STEP}
            value={options.imageQuality}
            disabled={disabled}
            aria-label="Image quality"
            onChange={(e) => onChange({ ...options, imageQuality: Number(e.target.value) })}
          />
          <span className={styles.qualityValue}>{options.imageQuality}%</span>
        </div>
      )}

      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={options.removeUnusedLayouts}
          disabled={disabled}
          onChange={(e) => onChange({ ...options, removeUnusedLayouts: e.target.checked })}
        />
        <span>Remove unused layouts</span>
      </label>
    </div>
  );
}

// ─── Toast ──────────────────────────────────────────────────────────────────

function ShareToast({ message }: Readonly<{ message: string }>) {
  return (
    <output className={styles.toast} aria-live="polite">
      <span className={styles.toastIcon} aria-hidden="true">✓</span>
      {message}
    </output>
  );
}
